import { CardState } from "../db";
import {
  basementInterval,
  getExperimentalInterval,
  getLevelForInterval,
  ReviewMarking,
  schedulesToIntervalSequences,
  SpacedRepetitionSchedule,
} from "../spacedRepetition";
import { CardType } from "../CardData";

function getUpdatedDueTimestamp({
  reviewTimestampMillis,
  intervalMillis,
  schedule,
  skipExperimentalBehavior,
}: {
  reviewTimestampMillis: number;
  intervalMillis: number;
  schedule: SpacedRepetitionSchedule;
  skipExperimentalBehavior: boolean;
}) {
  const scheduleSupportsExperimentalScheduleVariation =
    schedule === "original" ||
    schedule === "aggressiveStart" ||
    schedule === "default";

  // We manipulate the future due timestamp for experimental purposes, sometimes shrinking or expanding it so we can better understand memory decay.
  const effectiveIntervalMillis =
    !scheduleSupportsExperimentalScheduleVariation || skipExperimentalBehavior
      ? intervalMillis
      : getExperimentalInterval(reviewTimestampMillis, intervalMillis);
  return reviewTimestampMillis + effectiveIntervalMillis;
}

function getSupportsRetry({
  cardType,
  schedule,
  reviewTimestampMillis,
  skipExperimentalBehavior,
}: {
  cardType: CardType;
  schedule: SpacedRepetitionSchedule;
  reviewTimestampMillis: number;
  skipExperimentalBehavior: boolean;
}): boolean {
  const scheduleSupportsExperimentalRetrySuppression =
    schedule === "original" ||
    schedule === "aggressiveStart" ||
    schedule === "default";
  if (
    // 20% of the time, with these schedules, we skip the retry phase
    !scheduleSupportsExperimentalRetrySuppression ||
    reviewTimestampMillis % 10 > 2 ||
    skipExperimentalBehavior
  ) {
    return cardType !== "applicationPrompt";
  } else {
    return false;
  }
}

export default function updateCardStateForReviewMarking({
  baseCardState,
  cardType,
  reviewMarking,
  schedule,
  reviewTimestampMillis,
  fromMillis,
  generatedOrderSeed,
  skipExperimentalBehavior,
}: {
  baseCardState: CardState | null;
  cardType: CardType;
  reviewMarking: ReviewMarking;
  schedule: SpacedRepetitionSchedule;
  reviewTimestampMillis: number;
  fromMillis: (milliseconds: number) => CardState["dueTime"];
  generatedOrderSeed: number;
  skipExperimentalBehavior?: boolean;
}) {
  const supportsRetry = getSupportsRetry({
    cardType: cardType,
    schedule: schedule,
    reviewTimestampMillis: reviewTimestampMillis,
    skipExperimentalBehavior: skipExperimentalBehavior ?? false,
  });
  const currentReviewInterval = baseCardState ? baseCardState.interval : 0;
  const currentBestInterval = baseCardState ? baseCardState.bestInterval : null;
  const currentlyNeedsRetry =
    (baseCardState && baseCardState.needsRetry) || false;

  const intervalSequence = schedulesToIntervalSequences[schedule];

  const currentLevel = getLevelForInterval(currentReviewInterval, schedule);
  // The more aggressive schedules introduced in 2021/04 have a "basement" interval between in-text and the bottom level for forgotten prompts, i.e. so that people don't wait too long between reviews.
  const scheduleUsesSpecialBasementInterval = !(
    schedule === "default" ||
    schedule === "original" ||
    schedule === "aggressiveStart"
  );
  let newLevel: number | "basement";

  if (reviewMarking === "remembered") {
    if (currentlyNeedsRetry && currentLevel > 0) {
      // If the card needs to be retried, and the user remembers, then we just remove the retry indication. Unless they're on the "in-text" level, in which case we still bump them up to 5 days.
      newLevel = currentLevel;
    } else if (
      currentlyNeedsRetry &&
      scheduleUsesSpecialBasementInterval &&
      currentLevel === 0
    ) {
      newLevel = "basement";
    } else if (
      !baseCardState ||
      currentlyNeedsRetry ||
      // Our efficacy experiments sometimes have us adding cards arbitrarily. We'll only bump these cards' levels if they're 3/4 of the way through the repetition interval.
      reviewTimestampMillis >=
        baseCardState.dueTime.toMillis() - baseCardState.interval * 0.25
    ) {
      newLevel = Math.min(intervalSequence.length - 1, currentLevel + 1);
    } else {
      newLevel = currentLevel;
    }
  } else {
    if (currentLevel === 1) {
      newLevel = scheduleUsesSpecialBasementInterval ? "basement" : 1;
    } else if (currentLevel === 0) {
      if (currentReviewInterval === basementInterval) {
        newLevel = "basement";
      } else {
        newLevel = supportsRetry ? 0 : 1;
      }
    } else {
      newLevel = currentLevel - 1;
    }
  }

  const newInterval =
    newLevel === "basement"
      ? basementInterval
      : intervalSequence[newLevel].interval;

  let newDueTimestampMillis: number;
  let newOrderSeed: number;
  if (supportsRetry && reviewMarking === "forgotten") {
    newDueTimestampMillis = reviewTimestampMillis;
    newOrderSeed =
      baseCardState && baseCardState.needsRetry
        ? (baseCardState.orderSeed || generatedOrderSeed) + 1
        : generatedOrderSeed;
  } else {
    newDueTimestampMillis = getUpdatedDueTimestamp({
      reviewTimestampMillis: reviewTimestampMillis,
      intervalMillis: newInterval,
      schedule: schedule,
      skipExperimentalBehavior: skipExperimentalBehavior ?? false,
    });
    newOrderSeed = generatedOrderSeed;
  }

  let bestInterval: number | null;
  if (reviewMarking === "remembered") {
    if (
      currentlyNeedsRetry &&
      getLevelForInterval(currentReviewInterval, schedule) > 0
    ) {
      bestInterval = currentBestInterval;
    } else {
      bestInterval = Math.max(currentBestInterval || 0, currentReviewInterval);
    }
  } else {
    if (supportsRetry) {
      bestInterval = currentBestInterval;
    } else {
      bestInterval = currentBestInterval || 0;
    }
  }

  const newCardState: CardState = {
    dueTime: fromMillis(newDueTimestampMillis),
    bestInterval,
    interval: newInterval,
    needsRetry: supportsRetry && reviewMarking === "forgotten",
    reviewCount:
      baseCardState && baseCardState.reviewCount
        ? baseCardState.reviewCount + 1
        : 1,
    longestAttemptedInterval: currentReviewInterval,
    orderSeed: newOrderSeed,
  };
  return newCardState;
}
